import React, {createContext, useState, useEffect, useContext, useCallback} from 'react';
import axios from 'axios';
import {API_BASE_URL, API_KEY} from "../config/config";
import {useParams} from "react-router-dom";
import {AuthContext} from "./authContext";

const TagContext = createContext(null);

const TagProvider = ({ children }) => {
	const {visitor} = useContext(AuthContext);

	const { tagUrl} = useParams();

	const [tagLoading, setTagLoading] = useState(false);
	const [tag, setTag] = useState(null);

	const fetchTag = useCallback(async () => {
		if (!visitor) return;

		if (tag !== null) return; // Loaded

		if (tagLoading) return; // Loading

		try {
			setTagLoading(true);

			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/tags/${tagUrl}`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials : true
			};

			axios
				.request(config)
				.then((response) => {
					let tag = response.data.tag;
					setTag(tag);
					setTagLoading(false);
				});
		} catch (error) {
			console.error('Failed to fetch tag:', error);
			setTag([]);
			setTagLoading(false);
		}
	}, [tag, tagLoading, tagUrl, visitor]);

	const [threadsInitialLoaded, setThreadsInitialLoaded] = useState(false);
	const [threads, setThreads] = useState([]);
	const [threadsLoading, setThreadsLoading] = useState(false);
	const [threadsPage, setThreadsPage] = useState(1);
	const [hasMoreThreads, setHasMoreThreads] = useState(true);

	const fetchTagResults = useCallback(async (isInitial = false) => {
		if (!visitor) return;

		if (isInitial && threadsInitialLoaded) return; // Already loaded

		if (threadsLoading) return; // Loading

		try {
			setThreadsLoading(true);

			let config = {
				method: 'get',
				maxBodyLength: Infinity,
				url: `${API_BASE_URL}/tags/${tagUrl}/results?page=${threadsPage}`,
				headers: {
					'XF-Api-Key': API_KEY,
					'XF-Api-User': visitor.user_id,
				},
				withCredentials : true
			};

			axios
				.request(config)
				.then((response) => {
					let newThreads = response.data.threads;
					if (typeof newThreads === "object") {
						newThreads = Object.values(newThreads);
					}

					setThreads([...threads, ...newThreads]);

					let pagination = response.data.pagination;

					setHasMoreThreads(pagination.current_page < pagination.last_page);
					setThreadsPage(threadsPage + 1);
					setThreadsLoading(false);
					setThreadsInitialLoaded(true);
				});
		} catch (error) {
			console.error('Failed to fetch threads:', error);
			setThreadsLoading(false);
			setThreadsInitialLoaded(true);
		}
	}, [tagUrl, threads, threadsInitialLoaded, threadsLoading, threadsPage, visitor]);

	const loadMoreThreads = () => {
		if (!hasMoreThreads) {
			return;
		}

		setThreadsPage(threadsPage + 1);
		fetchTagResults(false);
	}

	useEffect(() => {
		fetchTag().then(r => {
			fetchTagResults(true);
		});
	}, [fetchTag, fetchTagResults, tagUrl, visitor]);

	return (
		<TagContext.Provider value={{
			tag,
			threads, threadsLoading, threadsPage, hasMoreThreads, loadMoreThreads,
		}}>
			{children}
		</TagContext.Provider>
	);
};

export { TagContext, TagProvider };
